import { useSearchParams } from 'react-router-dom';
import {
  TX_TYPE_LABEL_MAP,
  VoucherHistory,
  VoucherHistoryItem,
  VoucherItem,
} from '../../../constants/types/lms/voucher/type';
import { useAppDispatch } from '../../../store/hook';
import { useEffect, useMemo, useState } from 'react';
import { PaginationProps } from '../../../constants/types';
import {
  DEFAULT_PAGINATION,
  PAGE,
  PAGE_SIZE,
} from '../../../constants/constants';
import { getVoucherHistoryList } from '../../../api/utils/lms/voucher/api';
import { openAlert } from '../../../store/features/modalSlice';
import {
  formatToKstDate,
  getErrorMessages,
} from '../../../api/utils/util-func';
import Table, { ColumnsType } from 'antd/es/table';
import TextWinds from '../../common/TextWinds';
import { Button, DatePicker, Pagination, Tag } from 'antd';
import { numberWithCommas } from '../../../api/utils/format/numbers';
import dayjs from 'dayjs';
import VoucherHistoryStudentModal from './VoucherHistoryStudentModal';

export interface VoucherHistoryPanelProps {
  classId: string;
  selectedVoucher?: VoucherItem;
}

type HistoryFilter = 'all' | 'charge' | 'use';

const filterOptions: { label: string; value: HistoryFilter }[] = [
  { label: '전체', value: 'all' },
  { label: '적립만', value: 'charge' },
  { label: '사용만', value: 'use' },
];

/**
 * 개편 LMS (교사) - 이용권 관리 페이지 > 이용권 내역 판넬
 */
const VoucherHistoryPanel = ({
  classId,
  selectedVoucher,
}: VoucherHistoryPanelProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useAppDispatch();

  const [voucherHistory, setVoucherHistory] = useState<VoucherHistory | null>();
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>('all');
  const [pageInfo, setPageInfo] = useState<PaginationProps>(DEFAULT_PAGINATION);

  const [selectedVoucherHistoryItem, setSelectedVoucherHistoryItem] =
    useState<VoucherHistoryItem | null>(null);

  const { startDate, endDate, page, pageSize } = useMemo(() => {
    return {
      startDate: searchParams.get('startDate') || undefined,
      endDate: searchParams.get('endDate') || undefined,
      page: +searchParams.get('page') || PAGE,
      pageSize: +searchParams.get('pageSize') || PAGE_SIZE,
    };
  }, [classId, searchParams]);

  const fetchVoucherHistoryList = async () => {
    try {
      const response = await getVoucherHistoryList({
        classId,
        type: selectedVoucher.type,
        page,
        pageSize,
        filter: historyFilter === 'all' ? undefined : historyFilter,
        fromDate: startDate,
        toDate: endDate,
      });

      const { page: _page, ...rest } = response;

      setVoucherHistory(rest ?? null);
      setPageInfo(response.page);
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
      setVoucherHistory(null);
    }
  };

  useEffect(() => {
    if (!selectedVoucher) return;

    const isDateRangeValid = (startDate && !endDate) || (!startDate && endDate);

    if (isDateRangeValid) return;

    fetchVoucherHistoryList();
  }, [selectedVoucher, startDate, endDate, page, pageSize]);

  const filteredHistoryList = useMemo(() => {
    if (!voucherHistory) return [];

    return voucherHistory.result.filter((history) => {
      if (historyFilter === 'all') return true;

      const category = history.category === '사용' ? 'use' : 'charge';

      return category === historyFilter;
    });
  }, [voucherHistory, selectedVoucher, historyFilter]);

  const columns = (
    pageInfo: PaginationProps,
  ): ColumnsType<VoucherHistoryItem> => [
    {
      title: 'No.',
      key: 'index',
      render: (_, __, index) =>
        pageInfo.total - (pageInfo.page - 1) * pageInfo.pageSize - index,
      align: 'center',
      width: 60,
    },
    {
      title: '사용일',
      key: 'date',
      render: (_, row) => formatToKstDate(row.date, 'YYYY-MM-DD HH:mm'),
      align: 'center',
      className: 'min-w-[100px]',
    },
    {
      title: '구분',
      key: 'category',
      render: (_, row) => {
        switch (row.category) {
          case '사용':
            return <Tag color={'red'}>- {row.category}</Tag>;
          case '적립':
            return <Tag color={'green'}>+ {row.category}</Tag>;
          default:
            return '-';
        }
      },
      align: 'center',
      className: 'min-w-[100px]',
    },
    {
      title: '수량',
      key: 'amount',
      render: (_, row) => (
        <span
          className={[
            'shrink-0 text-sm font-semibold',
            row.category === '사용' ? 'text-red-500' : 'text-green-500',
          ].join(' ')}
        >
          {row.category === '사용'
            ? numberWithCommas(row.amount)
            : '+' + numberWithCommas(row.amount)}
        </span>
      ),
      align: 'center',
      className: 'min-w-[100px]',
    },
    {
      title: '유형',
      key: 'txTypeLabel',
      render: (_, row) => TX_TYPE_LABEL_MAP[row.txType],
      align: 'center',
      className: 'min-w-[100px]',
    },
    {
      title: '설명',
      key: 'todoInfo',
      render: (_, row) => row.todoInfo,
      align: 'center',
      className: 'min-w-[100px]',
    },
    {
      title: '상세',
      key: 'todoInfo',
      render: (_, row) => (
        <>
          {row.assignedTodoTxId && (
            <Button
              type="default"
              size="small"
              className={
                'h-6 rounded-lg !border-purple5 !bg-white px-3 text-xs font-bold !text-purple5 hover:!border-purple6 hover:!bg-purple1 hover:!text-purple6'
              }
              onClick={() => setSelectedVoucherHistoryItem(row)}
            >
              보기
            </Button>
          )}
        </>
      ),
      align: 'center',
      className: 'min-w-[100px]',
    },
  ];

  const handleClose = () => {
    setSelectedVoucherHistoryItem(null);
  };

  if (!selectedVoucher) {
    return (
      <div>
        <div className="mb-5 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <TextWinds type="title_h3">이용권 내역</TextWinds>
          </div>
        </div>

        <div className="flex h-[240px] items-center justify-center rounded-xl border border-dashed border-gray4 bg-gray0">
          <TextWinds type="content_body2" color="gray6">
            이용권을 선택해 주세요.
          </TextWinds>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <TextWinds type="title_h3">이용권 내역</TextWinds>

          <TextWinds
            type="content_body4"
            color="gray8"
            className="mt-1 block break-keep"
          >
            {selectedVoucher.name}
          </TextWinds>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-3 gap-2">
        <VoucherSummaryItem
          label="잔여 이용권"
          value={`${numberWithCommas(voucherHistory?.remaining || 0)}`}
          valueClassName="text-gray9"
        />
        <VoucherSummaryItem
          label="총 적립"
          value={`${numberWithCommas(voucherHistory?.totalCharged || 0)}`}
          valueClassName="text-green-500"
        />
        <VoucherSummaryItem
          label="총 사용"
          value={`${numberWithCommas(voucherHistory?.totalUsed || 0)}`}
          valueClassName="text-red-500"
        />
      </div>

      <div className="mb-4 flex flex-col gap-2 xl:flex-row xl:items-center">
        <div className="flex flex-wrap items-center gap-2">
          {filterOptions.map((option) => {
            const isActive = historyFilter === option.value;

            return (
              <button
                key={option.value}
                type="button"
                className={[
                  'rounded-full border px-3 py-1.5 transition-colors',
                  isActive
                    ? 'border-purple5 bg-purple5'
                    : 'border-gray4 bg-white hover:bg-gray1',
                ].join(' ')}
                onClick={() => setHistoryFilter(option.value)}
              >
                <TextWinds type="caption1" color={isActive ? 'white' : 'gray8'}>
                  {option.label}
                </TextWinds>
              </button>
            );
          })}
        </div>

        {/* 모바일 날짜 필터 */}
        <div className="grid w-full grid-cols-2 gap-2 xl:hidden">
          <DatePicker
            className="w-full rounded-lg"
            placeholder="시작일"
            value={startDate ? dayjs(startDate) : undefined}
            placement="bottomLeft"
            getPopupContainer={(triggerNode) =>
              triggerNode.closest('.voucher-history-drawer') ?? document.body
            }
            onChange={(date) => {
              const nextStartDate = date?.format('YYYY-MM-DD') ?? '';

              searchParams.set('startDate', nextStartDate);
              setSearchParams(searchParams);
            }}
            disabledDate={(current) => {
              if (!endDate) return false;
              return current.isAfter(endDate, 'day');
            }}
          />

          <DatePicker
            className="w-full rounded-lg"
            placeholder="종료일"
            value={endDate ? dayjs(endDate) : undefined}
            placement="bottomRight"
            getPopupContainer={(triggerNode) =>
              triggerNode.closest('.voucher-history-drawer') ?? document.body
            }
            onChange={(date) => {
              const nextEndDate = date?.format('YYYY-MM-DD') ?? '';

              searchParams.set('endDate', nextEndDate);
              setSearchParams(searchParams);
            }}
            disabledDate={(current) => {
              if (!startDate) return false;
              return current.isBefore(startDate, 'day');
            }}
          />
        </div>

        {/* PC 날짜 필터 */}
        <div className="hidden xl:block">
          <DatePicker.RangePicker
            className="w-full rounded-lg"
            placeholder={['시작일', '종료일']}
            value={
              startDate && endDate
                ? [dayjs(startDate), dayjs(endDate)]
                : [undefined, undefined]
            }
            onChange={(dates) => {
              searchParams.set(
                'startDate',
                dates?.[0]?.format('YYYY-MM-DD') ?? '',
              );
              searchParams.set(
                'endDate',
                dates?.[1]?.format('YYYY-MM-DD') ?? '',
              );
              setSearchParams(searchParams);
            }}
          />
        </div>
      </div>

      {filteredHistoryList.length === 0 ? (
        <div className="flex h-[200px] items-center justify-center rounded-xl border border-dashed border-gray4 bg-gray0">
          <TextWinds type="content_body2" color="gray6">
            조회된 이용 내역이 없습니다.
          </TextWinds>
        </div>
      ) : (
        <>
          <div className="flex flex-col overflow-hidden rounded-xl border border-gray2">
            <div className="flex flex-col gap-3">
              <Table
                dataSource={filteredHistoryList}
                columns={columns(pageInfo)}
                rowKey={(row) => row.id}
                rowClassName={(record) => {
                  return record.id === selectedVoucherHistoryItem?.id
                    ? 'bg-purple1'
                    : '';
                }}
                scroll={{ x: 'max-content', y: '300px' }}
                size="small"
                pagination={false}
              />
              <Pagination
                current={page}
                pageSize={pageSize}
                total={pageInfo.total}
                showSizeChanger
                onChange={(page, pageSize) => {
                  searchParams.set('page', page.toString());
                  searchParams.set('pageSize', pageSize.toString());
                  setSearchParams(searchParams);
                }}
                className="mb-3 justify-center"
              />
            </div>
          </div>

          {selectedVoucherHistoryItem &&
            selectedVoucherHistoryItem.assignedTodoTxId && (
              <VoucherHistoryStudentModal
                txId={selectedVoucherHistoryItem.assignedTodoTxId}
                onClose={handleClose}
              />
            )}
        </>
      )}
    </div>
  );
};

export default VoucherHistoryPanel;

type VoucherSummaryItemProps = {
  label: string;
  value: string;
  valueClassName: string;
};

const VoucherSummaryItem = ({
  label,
  value,
  valueClassName,
}: VoucherSummaryItemProps) => {
  return (
    <div className="rounded-xl bg-gray1 px-3 py-3 text-center">
      <TextWinds type="title_h3" className={valueClassName}>
        {value}
      </TextWinds>

      <TextWinds type="caption1" color="gray8" className="mt-1 block">
        {label}
      </TextWinds>
    </div>
  );
};
